// ─── Procedure scheduling capacity engine ─────────────────────────────────────
//
// Pure, dependency-free logic. Given a procedure template (a DAG of field-steps
// linked by edges that carry day-lags) plus per-step resource templates, it
// places each step on a calendar day relative to an anchor date and flags any
// resource that is booked beyond its daily capacity.
//
// This module has no React / network / Temporal imports so it can be unit tested
// in isolation — it is the riskiest logic in the feature.

import type {
  CgtFormType,
  ProcedureTemplateDetail,
  ResourceConflict,
  ScheduledBlock,
  SchedulingResource,
  StepResourceAllocation,
  StepScheduleTemplate,
  Procedure,
  ProcedureSchedule,
} from '@/types/cgt'

import { addDays, daysBetween } from './date'

/**
 * Resolve each step's day-offset from the anchor by walking the edge DAG.
 * A step's offset is the max over its incoming edges of
 * `predecessorOffset + minLagDays`; steps with no predecessors land on day 0.
 */
function computeDayOffsets(procedureTemplate: ProcedureTemplateDetail): Map<string, number> {
  const incoming = new Map<string, { pred: string; lag: number }[]>()
  for (const step of procedureTemplate.steps) incoming.set(step.stepId, [])
  for (const edge of procedureTemplate.edges) {
    const arr = incoming.get(edge.successorStepId)
    if (arr) arr.push({ pred: edge.predecessorStepId, lag: edge.minLagDays ?? 0 })
  }

  const memo = new Map<string, number>()
  const visiting = new Set<string>()

  const offset = (stepId: string): number => {
    const cached = memo.get(stepId)
    if (cached !== undefined) return cached
    if (visiting.has(stepId)) return 0 // cycle guard — treat as a root
    visiting.add(stepId)

    let value = 0
    for (const { pred, lag } of incoming.get(stepId) ?? []) {
      value = Math.max(value, offset(pred) + lag)
    }

    visiting.delete(stepId)
    memo.set(stepId, value)
    return value
  }

  for (const step of procedureTemplate.steps) offset(step.stepId)
  return memo
}

export interface BuildScheduleInput {
  procedure: Pick<Procedure, 'id' | 'patientMrn' | 'procedureTemplateId'>
  procedureTemplate: ProcedureTemplateDetail
  templates: StepScheduleTemplate[]
  resources: SchedulingResource[]
  /** ISO date (YYYY-MM-DD) the chain is anchored to. */
  anchorDate: string
}

/** Place every procedure-template step on the calendar and flag resource conflicts. */
export function buildSchedule(input: BuildScheduleInput): ProcedureSchedule {
  const { procedure, procedureTemplate, templates, resources, anchorDate } = input
  const templateByStep = new Map(templates.map((t) => [t.stepId, t]))
  const offsets = computeDayOffsets(procedureTemplate)

  const rawBlocks: ScheduledBlock[] = procedureTemplate.steps.map((step) => {
    const template = templateByStep.get(step.stepId)
    const dayOffset = offsets.get(step.stepId) ?? 0
    return {
      id: `${procedure.id}:${step.stepId}`,
      procedureId: procedure.id,
      stepId: step.stepId,
      label: step.formName,
      formType: step.formType ?? undefined,
      dayOffset,
      date: addDays(anchorDate, dayOffset),
      durationHours: template?.durationHours ?? 0,
      allocations: template?.allocations ?? [],
      hasConflict: false,
      conflictReasons: [],
    }
  })

  const { blocks, conflicts } = annotateConflicts(rawBlocks, resources)
  return {
    procedureId: procedure.id,
    patientMrn: procedure.patientMrn,
    procedureTemplateId: procedure.procedureTemplateId,
    anchorDate,
    blocks,
    conflicts,
  }
}

/** Find every (resource, day) pair booked beyond its daily capacity. */
export function detectConflicts(
  blocks: ScheduledBlock[],
  resources: SchedulingResource[],
): ResourceConflict[] {
  const capacityById = new Map(resources.map((r) => [r.id, r.capacityHoursPerDay]))
  const groups = new Map<
    string,
    { resourceId: string; date: string; hours: number; blockIds: string[] }
  >()

  for (const block of blocks) {
    for (const allocation of block.allocations) {
      const key = `${allocation.resourceId}|${block.date}`
      const group =
        groups.get(key) ??
        { resourceId: allocation.resourceId, date: block.date, hours: 0, blockIds: [] }
      group.hours += allocation.hours
      group.blockIds.push(block.id)
      groups.set(key, group)
    }
  }

  const conflicts: ResourceConflict[] = []
  for (const group of groups.values()) {
    const capacity = capacityById.get(group.resourceId) ?? Infinity
    if (group.hours > capacity) {
      conflicts.push({
        resourceId: group.resourceId,
        date: group.date,
        allocatedHours: group.hours,
        capacityHours: capacity,
        blockIds: group.blockIds,
      })
    }
  }
  return conflicts
}

/**
 * Detect conflicts and return a fresh block list with `hasConflict` /
 * `conflictReasons` set on the affected blocks. Never mutates its input.
 */
export function annotateConflicts(
  blocks: ScheduledBlock[],
  resources: SchedulingResource[],
): { blocks: ScheduledBlock[]; conflicts: ResourceConflict[] } {
  const conflicts = detectConflicts(blocks, resources)
  const nameById = new Map(resources.map((r) => [r.id, r.name]))
  const reasonsByBlock = new Map<string, string[]>()

  for (const conflict of conflicts) {
    const name = nameById.get(conflict.resourceId) ?? conflict.resourceId
    const message = `${name} over-allocated on ${conflict.date} (${conflict.allocatedHours}/${conflict.capacityHours} hrs)`
    for (const blockId of conflict.blockIds) {
      const list = reasonsByBlock.get(blockId) ?? []
      list.push(message)
      reasonsByBlock.set(blockId, list)
    }
  }

  const annotated = blocks.map((block) => ({
    ...block,
    hasConflict: reasonsByBlock.has(block.id),
    conflictReasons: reasonsByBlock.get(block.id) ?? [],
  }))
  return { blocks: annotated, conflicts }
}

export interface BlockMove {
  blockId: string
  /** Reschedule the whole block (all its allocations) to this ISO date. */
  toDate?: string
  /** Reassign a single allocation from one resource lane to another. */
  fromResourceId?: string
  toResourceId?: string
  /** Resize the block: new total duration in hours (allocations scale to match). */
  durationHours?: number
}

/**
 * Apply a drag/drop move to a schedule and recompute conflicts. Returns a new
 * schedule; the original is untouched. No dependency cascade in v1 — moving a
 * step does not shift its successors.
 */
export function applyMove(
  schedule: ProcedureSchedule,
  resources: SchedulingResource[],
  move: BlockMove,
): ProcedureSchedule {
  const moved = schedule.blocks.map((block) => {
    if (block.id !== move.blockId) return block

    const next: ScheduledBlock = {
      ...block,
      allocations: block.allocations.map((a) => ({ ...a })),
    }

    if (move.toDate) {
      next.date = move.toDate
      next.dayOffset = daysBetween(schedule.anchorDate, move.toDate)
    }

    if (
      move.fromResourceId &&
      move.toResourceId &&
      move.fromResourceId !== move.toResourceId
    ) {
      const from = move.fromResourceId
      const to = move.toResourceId
      const moving = next.allocations.find((a) => a.resourceId === from)
      if (moving) {
        const rest = next.allocations.filter((a) => a.resourceId !== from)
        const existing = rest.find((a) => a.resourceId === to)
        if (existing) {
          // Target lane already used by this step — merge hours instead of
          // creating a duplicate allocation on the same resource.
          next.allocations = rest.map((a) =>
            a.resourceId === to ? { ...a, hours: a.hours + moving.hours } : a,
          )
        } else {
          next.allocations = [...rest, { ...moving, resourceId: to }]
        }
      }
    }

    if (
      typeof move.durationHours === 'number' &&
      move.durationHours > 0 &&
      move.durationHours !== block.durationHours
    ) {
      // Scale each allocation proportionally so resource load tracks the new
      // duration (and conflicts recompute realistically).
      const factor = block.durationHours > 0 ? move.durationHours / block.durationHours : 1
      next.durationHours = move.durationHours
      next.allocations = next.allocations.map((a) => ({
        ...a,
        hours: Math.max(1, Math.round(a.hours * factor)),
      }))
    }

    return next
  })

  const { blocks, conflicts } = annotateConflicts(moved, resources)
  return { ...schedule, blocks, conflicts }
}

/** A full edit to one block — any omitted field is left unchanged. */
export interface BlockEdit {
  blockId: string
  label?: string
  /** ISO date (YYYY-MM-DD). */
  date?: string
  durationHours?: number
  allocations?: StepResourceAllocation[]
}

/**
 * Apply a detailed edit (label / date / duration / resource allocations) to a
 * block and recompute conflicts. Returns a new schedule; input is untouched.
 */
export function applyEdit(
  schedule: ProcedureSchedule,
  resources: SchedulingResource[],
  edit: BlockEdit,
): ProcedureSchedule {
  const edited = schedule.blocks.map((block) => {
    if (block.id !== edit.blockId) return block

    const next: ScheduledBlock = {
      ...block,
      allocations: block.allocations.map((a) => ({ ...a })),
    }

    if (edit.label !== undefined) next.label = edit.label
    if (edit.date) {
      next.date = edit.date
      next.dayOffset = daysBetween(schedule.anchorDate, edit.date)
    }
    if (typeof edit.durationHours === 'number' && edit.durationHours > 0) {
      next.durationHours = edit.durationHours
    }
    if (edit.allocations) {
      next.allocations = edit.allocations
        .filter((a) => a.resourceId && a.hours > 0)
        .map((a) => ({ ...a }))
    }

    return next
  })

  const { blocks, conflicts } = annotateConflicts(edited, resources)
  return { ...schedule, blocks, conflicts }
}

/** A brand-new ad-hoc block added straight onto the calendar (not from the procedure template). */
export interface NewBlockInput {
  label: string
  /** ISO date (YYYY-MM-DD) the block lands on. */
  date: string
  durationHours: number
  allocations: StepResourceAllocation[]
  /** Optional CGT form type — drives the block color only. */
  formType?: CgtFormType
}

/**
 * Add a new ad-hoc block to a schedule and recompute conflicts. The caller
 * supplies the block id (the engine stays free of id-generation side effects so
 * it remains pure and unit-testable). Returns a new schedule; input is untouched.
 */
export function addBlock(
  schedule: ProcedureSchedule,
  resources: SchedulingResource[],
  input: NewBlockInput,
  blockId: string,
): ProcedureSchedule {
  const block: ScheduledBlock = {
    id: blockId,
    procedureId: schedule.procedureId,
    // Ad-hoc blocks have no procedure step; reuse the id so it stays unique.
    stepId: blockId,
    label: input.label,
    formType: input.formType,
    dayOffset: daysBetween(schedule.anchorDate, input.date),
    date: input.date,
    durationHours: input.durationHours,
    allocations: input.allocations
      .filter((a) => a.resourceId && a.hours > 0)
      .map((a) => ({ ...a })),
    hasConflict: false,
    conflictReasons: [],
  }

  const { blocks, conflicts } = annotateConflicts([...schedule.blocks, block], resources)
  return { ...schedule, blocks, conflicts }
}

/**
 * Remove a block from a schedule and recompute conflicts (a deletion can clear a
 * double-booking elsewhere). Returns a new schedule; input is untouched.
 */
export function removeBlock(
  schedule: ProcedureSchedule,
  resources: SchedulingResource[],
  blockId: string,
): ProcedureSchedule {
  const remaining = schedule.blocks.filter((b) => b.id !== blockId)
  const { blocks, conflicts } = annotateConflicts(remaining, resources)
  return { ...schedule, blocks, conflicts }
}
